import { useEffect, useRef, useState } from 'react';
import { api } from './api.js';

// The Activity sidebar badge (ENTRAID-347 redesign, ia-proposal "Activity badge"): the number of unanswered critical
// Watch events of the last 7 days, the same count as the Activity page's KPI. Only for a viewer who reads Watch
// content (the customer's own admin, never an operator account): pass a null customerId otherwise and no request is
// made. A failed read shows no badge, never an error. Re-read at most once a minute while the user moves around.

const DAY = 86_400_000;
const MIN_GAP_MS = 60_000;

/** Unanswered critical directory events (admin notices excluded) of the last 7 days. */
export function unansweredCriticalCount(items, now = Date.now()) {
  return (items ?? []).filter((e) => e.noticeClass !== 'admin-notice' && e.severity === 'critical'
    && e.answerState === 'unanswered' && now - Date.parse(e.activityDateTime) <= 7 * DAY).length;
}

/**
 * @param {string|null} customerId the customer whose Watch content this viewer reads, or null
 * @param {string} [refreshKey] changes when a re-read may be due (the path)
 * @returns {{ count: number, more: boolean }|null}
 */
export function useActivityBadge(customerId, refreshKey = '') {
  const [state, setState] = useState({ customerId: null, value: null });
  const last = useRef({ customerId: null, at: 0 });
  const current = useRef(customerId);
  current.current = customerId;

  useEffect(() => {
    if (!customerId) return;
    const now = Date.now();
    if (last.current.customerId === customerId && now - last.current.at < MIN_GAP_MS) return;
    last.current = { customerId, at: now };
    // Only an answer for the customer still in view may land (a path change alone never drops it).
    const mine = () => current.current === customerId;
    Promise.resolve()
      .then(() => api.listWatchEvents(customerId, { from: new Date(now - 7 * DAY).toISOString() }))
      .then(
        (ev) => { if (mine()) setState({ customerId, value: { count: unansweredCriticalCount(ev?.items), more: Boolean(ev?.cursor) } }); },
        () => { if (mine()) setState({ customerId, value: null }); },
      );
  }, [customerId, refreshKey]);

  return customerId && state.customerId === customerId ? state.value : null;
}
